import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  CTDatatablePaginationParameter,
  CTGeneralService,
  CTModelDatatableFilter,
  CtBinaryOperator,
  CtButtonConfiguration,
  CtChipsControlOptions,
  CtControlConfiguration,
  CtControlService,
  CtControlTypes,
  CtDatatableContainerFilterValues,
  CtFormConfiguration,
  CtModelConfiguration,
  CtModelDatatableOperators,
  CtModelRouteData,
  CtModelService,
  CtSelectControlOptions,
  CtSelectControlValue,
  CtSortOrderDescriptorParameter,
  CtThemeTypes,
  MAT_RAISED_ACCENT,
  MAT_RAISED_WARN,
  MateriaHintExtensionConfig,
  MaterialControlOptions
} from '@ctsolution/ct-framework';
import { ActivatedRoute } from '@angular/router';
import { CtWebapiGenericResponse, DataRequest, MethodEnum } from '@ctsolution/ct-webapi';

import { CompanyTypeRouteData } from '../../../../_core/route-data/company-type.route-data';
import { LeadRouteData } from '../../../../_core/route-data/lead.route-data';
import { CTConfigurationModel } from '../../../../_core/classes/ct-configuration-model';
import { CustomerTypeEnum } from '../../../../_core/enum/customer-type.enum';
import { CTMGeneralService } from '../../../../_core/lib/general.service';
import { CtManagerService } from '../../../../_core/lib/ct-manager.service';
import { CustomCtFormContainerHelper } from '../../../../_core/services/custom-ct-form.helper';
import { ContactInformationTypeVO, LeadInformationComponent } from '../lead-information/lead-information.component';
import { LeadInformationFieldConfig } from '../../../../_core/classes/ct-configuration-model';
import { LeadInformationFormService } from '../lead-information-form/lead-information-form.service';
import { LeadInformation } from '../lead-information-form/lead-information';
import { LeadInformationRouteData } from '../../../../_core/route-data/lead-information.route-data';

@Component({
  selector: 'app-lead-edit-form',
  templateUrl: './lead-edit-form.component.html',
  styleUrls: ['./lead-edit-form.component.scss']
})
export class LeadEditFormComponent implements OnInit, AfterViewInit {

  // ─── Input / Output ───────────────────────────────────────────────────────

  @Output() onSubmit: EventEmitter<FormGroup | null> = new EventEmitter<FormGroup | null>();

  /**
   * Emette `false` quando viene selezionato un Customer (la sezione luoghi
   * del lead non deve essere mostrata), `true` quando viene deselezionato.
   */
  @Output() valueChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() masterDetail = false;
  @Input() leadOid: number | null = null;
  @Input() datatableContainerFilterValues: CtDatatableContainerFilterValues | null = null;

  /**
   * Configurazione del model lead padre, passata da `LeadEditComponent`.
   * Necessaria per far sapere a `LeadInformationFormComponent` l'OID del lead
   * senza dover ripetere la chiamata getInfos.
   */
  @Input() leadMasterConfiguration: CtModelConfiguration<any> | null = null;

  @ViewChild(LeadInformationComponent) leadInformationComponent: LeadInformationComponent | undefined;

  // ─── Stato del componente ─────────────────────────────────────────────────

  companyOid: number | null = null;

  /**
   * `true` se lo stato corrente del lead è "Completato" (Type == 4).
   * Controlla la visibilità del pulsante "Crea Work Order".
   */
  leadStatusComplete = false;

  /**
   * Override per-client: se `true` il pulsante "Crea Work Order" è sempre
   * visibile indipendentemente dallo stato del lead.
   */
  bypassLeadStatus = false;

  configuration: CtFormConfiguration;

  // ─── Bottoni form ─────────────────────────────────────────────────────────

  cancelButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel('CT_PAGE.CT_FORM.cancel')
    .setAction(() => this.ctGeneralService.back())
    .setMatherialOptions(MAT_RAISED_WARN);

  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel('CT_PAGE.CT_FORM.save')
    .setClass('ct-model-submit-button')
    .setAction(() => this.onSubmit.emit(this.configuration.form))
    .setMatherialOptions(MAT_RAISED_ACCENT);

  // ─── Definizione controlli ────────────────────────────────────────────────

  // Nota: i controlli vengono dichiarati come proprietà private per permettere
  // la composizione dinamica del layout (filtro dell'array `controls` in base
  // al tipo cliente). Ogni modifica di visibilità avviene ricreando
  // `CtFormConfiguration` con un sottoinsieme diverso dell'array.

  private nameControl = this.generalService.getTextControl({
    name: 'Name',
    required: true
  });

  private surnameControl = this.generalService.getTextControl({
    name: 'Surname',
    required: true
  });

  private phoneControl = this.generalService.getTextControl({
    name: 'Phone',
    required: false
  });

  private emailControl = this.generalService.getTextControl({
    name: 'Email',
    required: false
  });

  private noteControl = this.generalService.getTextControl({
    name: 'Note',
    required: false
  })
    .setFlexSpacePercentage(100)
    .setType(CtControlTypes.TEXTAREA);

  private codeControl = this.generalService.getTextControl({
    name: 'Code',
    required: false
  })
    .setFlexSpacePercentage(50)
    .setHidden(true);

  private documentDateControl = this.generalService.getDateControlDefault({
    name: 'DocumentDate',
    required: true
  })
    .setFlexSpacePercentage(50)
    .setValue(new Date());

  // Campi visibili solo per clienti BUSINESS (nascosti per PRIVATE)
  private vatControl = this.generalService.getTextControl({
    name: 'VATNUMBER',
    required: false
  }).setFlexSpacePercentage(50);

  private sdiControl = this.generalService.getTextControl({
    name: 'SDICODE',
    required: false
  }).setFlexSpacePercentage(33);

  private paymentMethodControl = this.generalService.getTextControl({
    name: 'PaymentMethod',
    required: false
  }).setFlexSpacePercentage(33);

  private billingEmailControl = this.generalService.getTextControl({
    name: 'BillingEmail',
    required: false
  }).setFlexSpacePercentage(33);

  private leadTypeControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'LeadType',
    controller: 'LeadType',
    type: CtControlTypes.ENUMERABLE
  }).setFlexSpacePercentage(50);

  private leadSourceControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'LeadSource',
    controller: 'LeadSource',
    type: CtControlTypes.ENUMERABLE,
    required: false
  }).setFlexSpacePercentage(33.33);

  private leadStatusControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'LeadStatus',
    controller: 'LeadStatus',
    type: CtControlTypes.ENUMERABLE,
    key: 'OrderIndex'
  }).setFlexSpacePercentage(33.33);

  private leadInterestControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'LeadInterest',
    controller: 'LeadInterest',
    type: CtControlTypes.ENUMERABLE,
    required: false
  }).setFlexSpacePercentage(33.33);

  /**
   * Lookup per l'azienda cliente.
   * Ricerca su più campi (FiscalCode, Name, VAT, Surname, ReferenceName)
   * con OR, filtrando solo le company attive.
   */
  private customerControl: CtControlConfiguration =
    this.controlService.getDefaultConfiguration({
      name: 'Customer',
      label: 'CustomerCompiled',
      type: CtControlTypes.LOOKUP,
      remoteValueOptionsEndpoint: DataRequest
        .create()
        .setController(['Company'])
        .setAction('LookupForCustomers')
        .setMethod(MethodEnum.POST)
        .setQueryParams({ hideSpinner: true })
    })
      .setNullValueEnabled(true)
      .setTheme(CtThemeTypes.MATERIAL)
      .configureOptions<CtSelectControlOptions>(options => {

        if (!options) options = CtSelectControlOptions.create();

        options
          .setNullValueEnabled(false)
          .setLookupFilter((value) => {

            const filters: CTModelDatatableFilter[] = [];

            if (value) {
              // Ricerca testuale multi-campo in OR
              const searchableFields = [
                '[FiscalCode]', '[Name]', '[VAT]', '[Surname]', '[ReferenceName]'
              ];

              searchableFields.forEach(field => {
                filters.push(
                  CTModelDatatableFilter
                    .create()
                    .setField(`${field} is not null AND Contains(${field}, ?)`)
                    .setValue([value])
                    .setOperatorType(CtBinaryOperator.OrOperator)
                );
              });
            }

            // Solo company attive
            filters.push(
              CTModelDatatableFilter
                .create()
                .setField('Active')
                .setValue(true)
                .setOperatorType(CtBinaryOperator.Equal)
            );

            const operators = CtModelDatatableOperators.create().setFilters(filters);
            operators.Pagination?.setSortOrders([
              CtSortOrderDescriptorParameter.create('Name')
            ]);

            return operators;
          });
      });

  private customerTypeControl = this.generalService.getLookupControlDefaultConfiguration({
    name: 'CustomerType',
    controller: 'CompanyType',
    type: CtControlTypes.ENUMERABLE
  });

  /**
   * Select per la sede aziendale interna (CompanyPlace).
   * Le opzioni vengono popolate da `_loadSelfCompanyPlaces()`.
   */
  private companyPlaceControl: CtControlConfiguration =
    this.controlService.getDefaultConfiguration({
      name: 'CompanyPlace',
      label: 'CompanyPlace',
      type: CtControlTypes.ENUMERABLE
    })
      .setTheme(CtThemeTypes.MATERIAL)
      .setFlexSpacePercentage(50);

  /** Chips per i tag: supporta la creazione inline di nuovi tag. */
  private tagControl: CtControlConfiguration = CtControlConfiguration
    .create()
    .setName('Tags')
    .setLabel('TAG')
    .setType(CtControlTypes.CHIPS)
    .setTheme(CtThemeTypes.MATERIAL)
    .setRemoteValueOptionsEndpoint(
      DataRequest
        .create()
        .setUrl('/Tag/Lookup')
        .setMethod(MethodEnum.POST)
        .setBody(CtModelDatatableOperators.create())
    )
    .configureOptions<CtChipsControlOptions>(options => {

      if (!options) options = CtChipsControlOptions.create();

      options.setOnAdd((event: string) =>
        new Promise<CtWebapiGenericResponse<number | string> | null>(resolve => {

          const routeData = CtModelRouteData.create().setController('tag');
          const configuration = CtModelConfiguration.create().setRouteData(routeData);

          this.modelService
            .putInfos(configuration, { content: event })
            ?.subscribe((response: CtWebapiGenericResponse<any>) => resolve(response.Result));

        })
      );

    })
    .setFlexSpacePercentage(50);

  // ─── Stato interno (non esposto al template) ──────────────────────────────

  /** Tipo cliente attuale, tracciato per guidare la composizione del layout. */
  private customerType: CustomerTypeEnum | null = null;

  /** Dati grezzi della company selezionata nel lookup Customer. */
  private company: any = null;

  /**
   * Tag da ripristinare al prossimo `_rebuildForm()`.
   * Viene popolato da `setup()` prima che il rebuild venga scatenato
   * dal `customerTypeControl.setValue()`, e consumato (e azzerato) dentro
   * `_rebuildForm()` per garantire che i tag sopravvivano alla ricreazione
   * del FormGroup indipendentemente dai tempi del setTimeout.
   */
  private _pendingTags: CtSelectControlValue[] | null = null;

  /**
   * Mappa dei controlli LeadInformation aggiunti dinamicamente al form.
   * Chiave: Description del tipo (es. "FirstPhone"), Valore: Code numerico (es. "1").
   * Usata al submit per separare questi valori dal DTO del lead e salvarli su leadinformation.
   */
  leadInformationControlMap: Map<string, string> = new Map();

  /**
   * DataSource dei record LeadInformation già esistenti per questo lead.
   * Caricato in setup() e usato al submit per distinguere insert da update.
   */
  leadInformationDataSource: LeadInformation[] = [];

  /**
   * Elenco completo dei controlli nell'ordine di rendering desiderato.
   * La configurazione del form filtra questo array in base al tipo cliente.
   */
  private readonly allControls = [
    this.documentDateControl,
    this.leadTypeControl,
    this.customerControl,
    this.customerTypeControl,
    this.nameControl,
    this.surnameControl,
    this.phoneControl,
    this.emailControl,
    this.vatControl,
    this.sdiControl,
    this.paymentMethodControl,
    this.billingEmailControl,
    this.leadSourceControl,
    this.leadStatusControl,
    this.leadInterestControl,
    this.companyPlaceControl,
    this.tagControl,
    this.noteControl,
    this.codeControl
  ];

  // ─────────────────────────────────────────────────────────────────────────

  constructor(
    private generalService: CTMGeneralService,
    private ctGeneralService: CTGeneralService,
    private formBuilder: FormBuilder,
    private controlService: CtControlService,
    private route: ActivatedRoute,
    private modelService: CtModelService<any>,
    private managerService: CtManagerService,
    private leadInformationFormService: LeadInformationFormService
  ) {
    // Configurazione iniziale: mostriamo il layout PRIVATE come default
    // (senza VAT, SDI, BillingEmail, PaymentMethod)
    this.configuration = this._buildFormConfiguration(CustomerTypeEnum.PRIVATE);
    this.generalService.syncMissingControls(this.allControls, LeadRouteData(), this.configuration);
    this._applyCustomerTypeLayout(null, null);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this._initLeadStatusHint();
    this._loadSelfCompanyPlaces();
    this._initLeadInformationControls();
  }

  ngAfterViewInit(): void {
    this._subscribeToCustomerChanges();
    this._subscribeToCustomerTypeChanges();
    this._applyQueryStringFilters();
  }

  // ─── Inizializzazione ─────────────────────────────────────────────────────

  /** Aggiunge l'hint informativo al controllo LeadStatus. */
  private _initLeadStatusHint(): void {
    if (!this.leadStatusControl.materialOptions) {
      this.leadStatusControl.setMaterialOptions(MaterialControlOptions.create());
    }
    this.leadStatusControl.materialOptions
      ?.setHint(MateriaHintExtensionConfig.create().setMessage('leadStatusControlHint'));
  }

  /**
   * Carica le sedi dell'azienda interna (IsSelf = true) per popolare
   * le opzioni del companyPlaceControl. Se c'è una sola sede, la precompila.
   */
  private _loadSelfCompanyPlaces(): void {
    this.generalService.getList({
      controller: 'Company',
      filters: [
        CTModelDatatableFilter
          .create()
          .setField('IsSelf')
          .setValue(true)
          .setOperatorType(CtBinaryOperator.Equal)
      ],
      pagination: new CTDatatablePaginationParameter(1, 100)
    }).subscribe((response: CtWebapiGenericResponse<any>) => {

      if (!response.Result.DataSource?.length) return;

      const firstResult = response.Result.DataSource[0];
      const referenceName = firstResult.ReferenceName ?? '';

      const options: CtSelectControlValue[] = firstResult.Places.map((place: any) => {
        const label = `${referenceName} - ${place.Name ?? ''}, ${place.City ?? ''}`;
        return CtSelectControlValue.create().setLabel(label).setValue(place.Oid);
      });

      this.companyPlaceControl.setValueOptions(options);

      if (options.length === 1) {
        this.companyPlaceControl.setValue(options[0].value);
      }
    });
  }

  // ─── LeadInformation controls ──────────────────────────────────────────────

  /**
   * Carica la configurazione per-client e, se abilitata, aggiunge i controlli
   * LeadInformation direttamente nell'`allControls` prima del noteControl.
   *
   * I controlli vengono creati come semplici campi di testo con nome uguale
   * alla Description del tipo (es. "FirstPhone"). La `leadInformationControlMap`
   * tiene traccia del mapping Description → Code per il submit.
   *
   * ⚠️ Deve essere chiamato in ngOnInit, prima che il form venga ricostruito
   * per la prima volta via _applyCustomerTypeLayout.
   */
  private async _initLeadInformationControls(): Promise<void> {

    const cfg = CTConfigurationModel.create();
    await this.managerService.getComponentOverride('lead', 'leadInformationConfiguration', cfg);

    if (!cfg.leadInformationEnabled || !cfg.leadInformationFields.length) return;

    this.leadInformationFormService.getLeadInformationTypes().subscribe(allTypes => {

      // Costruiamo una mappa type → config con chiave string (allineata a CtLookupItem.Code)
      const fieldConfigMap = new Map<string, LeadInformationFieldConfig>(
        cfg.leadInformationFields.map(f => [String(f.type), f])
      );

      const enabledCodes = cfg.leadInformationFields.map(f => String(f.type));
      const filteredTypes = allTypes.filter(t => enabledCodes.includes(String(t.Code)));

      if (!filteredTypes.length) return;

      // Indice di inserimento: prima di noteControl (penultimo prima di codeControl)
      const insertBeforeIndex = this.allControls.indexOf(this.noteControl);

      filteredTypes.forEach(t => {
        if (!t.Description || !t.Code) return;

        // Evita duplicati in caso di chiamate multiple
        if (this.leadInformationControlMap.has(t.Description)) return;

        // Recupera la configurazione del campo; applica default se non specificata
        // t.Code è string | null (CtLookupItem) — il null è già escluso dalla guard sopra
        const fieldCfg = fieldConfigMap.get(t.Code);
        const controlType    = fieldCfg?.controlType    ?? CtControlTypes.TEXT;
        const flexPercentage = fieldCfg?.flexPercentage ?? 50;

        const control = this.generalService.getTextControl({
          name: t.Description,
          required: false
        })
          .setType(controlType)
          .setFlexSpacePercentage(flexPercentage);

        // Inserisce il controllo prima del noteControl nell'array allControls
        this.allControls.splice(insertBeforeIndex + this.leadInformationControlMap.size, 0, control);

        // Registra il mapping Description → Code per il submit
        this.leadInformationControlMap.set(t.Description, String(t.Code));
      });

      // Ricostruisce il form includendo i nuovi controlli
      this._rebuildForm(this.customerType);

    });

  }

  /**
   * Carica i record LeadInformation esistenti per il lead corrente
   * e pre-compila i controlli corrispondenti nel form.
   * Chiamato da `setup()` dopo il patch dei valori base.
   *
   * @param leadOid OID del lead già salvato
   */
  loadLeadInformationValues(leadOid: number): void {

    if (!this.leadInformationControlMap.size) return;

    this.generalService.getList({
      controller: LeadInformationRouteData().controller!,
      filters: [
        CTModelDatatableFilter
          .create()
          .setField('Lead.Oid')
          .setValue(leadOid.toString())
          .setOperatorType(CtBinaryOperator.Equal)
      ],
      pagination: new CTDatatablePaginationParameter(1, 100)
    }).subscribe((response: CtWebapiGenericResponse<any>) => {

      this.leadInformationDataSource = response?.Result?.DataSource ?? [];

      // Pre-compila ogni controllo LeadInformation con il valore esistente.
      // Type è number nel modello, code è stringa nel Map — convertiamo per confronto sicuro.
      this.leadInformationDataSource.forEach((record: LeadInformation) => {

        this.leadInformationControlMap.forEach((code, description) => {
          if (record.Type === parseInt(code, 10)) {
            this.configuration.form?.get(description)?.setValue(record.Content);
          }
        });

      });

    });

  }

  // ─── Subscriptions valueChanges ───────────────────────────────────────────

  /**
   * Reagisce alla selezione/deselezione del Customer nel lookup.
   * - Selezione: carica i dati della company e precompila il form.
   * - Deselezione: ripristina i campi liberi e resetta la sezione indirizzi.
   */
  private _subscribeToCustomerChanges(): void {
    this.customerControl.control?.valueChanges.subscribe(result => {

      if (!result?.value) {
        this.company = null;
        this.companyOid = null;
        this.leadInformationComponent?.reset();
        this.valueChanged.emit(true);
        this._applyCustomerTypeLayout(null, this.customerType);
        return;
      }

      this.valueChanged.emit(false);

      this.generalService.getInfos({
        controller: 'Company',
        oid: result.value
      }).subscribe((response: CtWebapiGenericResponse<any>) => {

        this.company = response.Result.DataSource;
        if (!this.company) return;

        this.companyOid = this.company.Oid;
        this.leadInformationComponent?.setup(this.companyOid);
        this.customerTypeControl.setValue(this.company.Type.Oid);
        this.customerType = this.company.Type.Type;
        this._applyCustomerTypeLayout(this.company, this.customerType);
        this._applyLeadSourceFilter(this.company.Type.Type);

      });
    });
  }

  /** Reagisce al cambio manuale del CustomerType (solo quando non c'è un Customer selezionato). */
  private _subscribeToCustomerTypeChanges(): void {
    this.customerTypeControl.control?.valueChanges.subscribe(value => {
      this._loadAndApplyCustomerType(value);
    });
  }

  // ─── Logica CustomerType ──────────────────────────────────────────────────

  /**
   * Carica i dati del CustomerType dal server e aggiorna il layout del form.
   * Se `customerTypeOid` è null, applica il default definito via override per-client
   * (default: PRIVATE).
   */
  private _loadAndApplyCustomerType(customerTypeOid: number | null): void {
    const configuration = CtModelConfiguration.create().setRouteData(CompanyTypeRouteData());
    const operators = CtModelDatatableOperators.create();

    if (customerTypeOid) {
      operators.setFilters([
        CTModelDatatableFilter
          .create()
          .setField('Oid')
          .setValue(customerTypeOid)
          .setOperatorType(CtBinaryOperator.Equal)
      ]);
    }

    this.modelService.getList(configuration, operators)
      .subscribe(async (response: CtWebapiGenericResponse<any>) => {

        if (!customerTypeOid) {
          // Nessun tipo selezionato: applica il default per-client (es. PRIVATE)
          const defaultTypeCfg = CTConfigurationModel.create().setType(CustomerTypeEnum.PRIVATE);
          await this.managerService.getComponentOverride('lead', 'companyType', defaultTypeCfg);

          const defaultEntry = response.Result.DataSource
            .find((el: any) => el.Type === defaultTypeCfg.type);

          if (defaultEntry) {
            this.customerTypeControl.setValue(defaultEntry.Oid);
            this.customerType = defaultEntry.Type;
          }
        }

        const result = response.Result.DataSource[0];
        if (!result) return;

        this.customerType = result.Type;
        this._applyCustomerTypeLayout(this.company, this.customerType);
      });
  }

  // ─── Layout dinamico del form ─────────────────────────────────────────────

  /**
   * Punto di ingresso unico per aggiornare il layout del form.
   * Gestisce sia il caso "customer selezionato" (campi disabilitati + precompilati)
   * sia il caso "nessun customer" (campi liberi).
   *
   * @param company  Dati grezzi della company selezionata, o null
   * @param customerType  Tipo cliente corrente
   */
  private async _applyCustomerTypeLayout(
    company: any | null,
    customerType: CustomerTypeEnum | null
  ): Promise<void> {

    // Override per-client sul bypass dello stato lead (solo BUSINESS)
    if (this.customerType === CustomerTypeEnum.BUSINESS) {
      const bypassCfg = CTConfigurationModel.create();
      await this.managerService.getComponentOverride('lead', 'bypassLeadStatusConfiguration', bypassCfg);
      this.bypassLeadStatus = bypassCfg.bypassLeadStatus;

      // Se il bypass è attivo, rimuoviamo l'hint che invita a completare il lead
      const hintMessage = this.bypassLeadStatus ? '' : 'leadStatusControlHint';
      this.leadStatusControl.materialOptions
        ?.setHint(MateriaHintExtensionConfig.create().setMessage(hintMessage));
    }

    if (!this.configuration.controls) return;

    if (company) {
      // ── Con customer selezionato: campi anagrafici disabilitati e precompilati ──
      this._setAnagraficaDisabled(true);
      this._prefillFromCompany(company, customerType);
    } else {
      // ── Senza customer: campi anagrafici abilitati e form ricostruito ──
      this._setAnagraficaDisabled(false);
      this.customerControl.setFlexSpacePercentage(50);
      this._rebuildForm(customerType);
    }

    this.configuration.form?.updateValueAndValidity();
  }

  /**
   * Abilita/disabilita i campi anagrafici che si bloccano quando
   * è selezionato un Customer dalla lookup.
   */
  private _setAnagraficaDisabled(disabled: boolean): void {
    this.nameControl.setDisabled(disabled);
    this.surnameControl.setDisabled(disabled);
    this.phoneControl.setDisabled(disabled);
    this.emailControl.setDisabled(disabled);
    this.vatControl.setDisabled(disabled);
    this.sdiControl.setDisabled(disabled);
    this.paymentMethodControl.setDisabled(disabled);
    this.billingEmailControl.setDisabled(disabled);

    if (disabled) {
      this.customerTypeControl.control?.disable({ emitEvent: false });
    } else {
      this.customerTypeControl.control?.enable({ emitEvent: false });
    }
  }

  /**
   * Precompila i campi anagrafici dai dati della company selezionata
   * e ricostruisce il form nel layout corretto per il tipo cliente.
   */
  private _prefillFromCompany(company: any, customerType: CustomerTypeEnum | null): void {

    this.nameControl.setValue(company?.Name);
    this.paymentMethodControl.setValue(company?.PaymentMethod);

    // Recupera le informazioni di contatto (email, telefono, SDI, ecc.)
    this.generalService.getList({
      controller: 'CompanyInformation',
      filters: [
        CTModelDatatableFilter
          .create()
          .setField('Company.Oid')
          .setValue(company.Oid.toString())
          .setOperatorType(CtBinaryOperator.Equal)
      ],
      pagination: new CTDatatablePaginationParameter(1, 100)
    }).subscribe((response: CtWebapiGenericResponse<any>) => {

      response.Result.DataSource?.forEach((element: any) => {
        switch (element.Type) {
          case ContactInformationTypeVO.FirstEmail:   this.emailControl.setValue(element.Content);         break;
          case ContactInformationTypeVO.FirstPhone:   this.phoneControl.setValue(element.Content);         break;
          case ContactInformationTypeVO.SDI:          this.sdiControl.setValue(element.Content);           break;
          case ContactInformationTypeVO.BillingEmail: this.billingEmailControl.setValue(element.Content);  break;
        }
      });
    });

    // Layout specifico per tipo cliente + precompilazione campi extra BUSINESS
    if (customerType === CustomerTypeEnum.PRIVATE) {
      this.nameControl.setLabel('Name');
      this.tagControl.setLabel('TagPrivate');
      this.surnameControl.setValue(company?.Surname);
    } else {
      this.nameControl.setLabel('CompanyName');
      this.tagControl.setLabel('TagBusiness');
      this.nameControl.setValue(company?.ReferenceName);
      this.vatControl.setValue(company?.VAT);
      this.sdiControl.setValue(company?.SDI);
      this.billingEmailControl.setValue(company?.BillingEmail);
      this.paymentMethodControl.setValue(company?.PaymentMethod);
    }

    this._rebuildForm(customerType);
  }

  /**
   * Ricostruisce `CtFormConfiguration` con il sottoinsieme di controlli
   * appropriato per il tipo cliente indicato.
   *
   * ⚠️ La ricreazione del FormGroup distrugge tutti i FormControl esistenti.
   * È necessario preservare manualmente i valori dei controlli che non vengono
   * re-inizializzati altrove (es. Tags, CompanyPlace) per non perderli.
   *
   * PRIVATE  → nasconde VAT/SDI/BillingEmail/PaymentMethod
   * BUSINESS → nasconde Surname
   */
  private _rebuildForm(customerType: CustomerTypeEnum | null): void {
    // Preserva i valori che sopravvivono al rebuild e che non vengono
    // riassegnati esplicitamente dopo (es. da setup() o da _prefillFromCompany()).
    // Per i tag usiamo _pendingTags se disponibile (popolato da setup() prima del rebuild),
    // altrimenti prendiamo il valore corrente del FormControl.
    const preservedTags        = this._pendingTags ?? this.tagControl.control?.value ?? null;
    const preservedCompanyPlace = this.companyPlaceControl.control?.value ?? null;

    this.configuration = this._buildFormConfiguration(customerType);

    // Ripristina CompanyPlace nel nuovo FormControl
    if (preservedCompanyPlace !== null) {
      this.companyPlaceControl.control?.setValue(preservedCompanyPlace, { emitEvent: false });
    }

    // Ripristina i tag: se provengono da _pendingTags (caricamento iniziale da setup),
    // usiamo notifyValuesUpdated con setTimeout per attendere che il chips component
    // si sia inizializzato sul nuovo form group. Se invece provengono dal FormControl
    // esistente (rebuild per cambio tipo), li scriviamo direttamente.
    if (preservedTags?.length) {
      if (this._pendingTags) {
        // Caso: caricamento iniziale da setup() — il chips component potrebbe non
        // essere ancora montato sul nuovo form group, serve il setTimeout
        setTimeout(() => {
          (<CtChipsControlOptions>this.tagControl.options)?.notifyValuesUpdated(preservedTags);
        }, 500);
        this._pendingTags = null; // consumato
      } else {
        // Caso: rebuild per cambio tipo durante la sessione — il chips component
        // è già montato, scriviamo direttamente sul FormControl
        this.tagControl.control?.setValue(preservedTags, { emitEvent: false });
      }
    } else {
      this._pendingTags = null; // pulizia precauzionale
    }
  }

  /**
   * Costruisce una nuova `CtFormConfiguration` filtrando i controlli
   * in base al tipo cliente.
   */
  private _buildFormConfiguration(customerType: CustomerTypeEnum | null): CtFormConfiguration {
    const businessOnlyControls = [
      this.vatControl,
      this.sdiControl,
      this.billingEmailControl,
      this.paymentMethodControl
    ];

    let controls: CtControlConfiguration[];

    if (customerType === CustomerTypeEnum.BUSINESS) {
      // BUSINESS: include VAT/SDI/BillingEmail/PaymentMethod, esclude Surname
      controls = this.allControls.filter(c => c !== this.surnameControl);
    } else {
      // PRIVATE o null: esclude i campi business-only
      controls = this.allControls.filter(c => !businessOnlyControls.includes(c));
    }

    return CtFormConfiguration
      .create(this.formBuilder.group({}))
      .setTheme(CtThemeTypes.MATERIAL)
      .setControls(controls);
  }

  // ─── Filtro LeadSource per tipo cliente ───────────────────────────────────

  /**
   * Filtra le opzioni di LeadSource in base al tipo cliente:
   * mostra le fonti senza tipo specifico (null) oppure quelle compatibili
   * con il CustomerType corrente.
   */
  private _applyLeadSourceFilter(customerType: CustomerTypeEnum): void {
    this.leadSourceControl
      .configureOptions<CtSelectControlOptions>(options => {

        if (!options) options = CtSelectControlOptions.create();

        options
          .setNullValueEnabled(false)
          .setLookupFilter(() => {
            const operators = CtModelDatatableOperators.create();
            operators.setFilters([
              CTModelDatatableFilter
                .create()
                .setField('[LeadSourceType] is null OR [LeadSourceType.CompanyType.Type] = ?')
                .setValue([customerType])
                .setOperatorType(CtBinaryOperator.OrOperator)
            ]);
            return operators;
          })
          ?.setLookupResponseMapper((response: CtWebapiGenericResponse<any>) => {
            return (response.Result.DataSource ?? [])
              .map((elm: any) => CtSelectControlValue.create().setLabel(elm.Description).setValue(elm.Code));
          });
      });

    (<CtSelectControlOptions>this.leadSourceControl.options).notifyValuesUpdated();
  }

  // ─── Filtri da query string ───────────────────────────────────────────────

  /**
   * Applica i filtri provenienti dalla query string (?q=...).
   * Permette alla lista lead di pre-configurare il form (es. tipo cliente,
   * tipo lead) quando si naviga alla creazione di un nuovo lead.
   */
  private _applyQueryStringFilters(): void {
    const customerTypeValue = this._getFilterValue('CustomerType.Type', 'Customer.Type.Type');
    if (customerTypeValue) {
      this._setupCustomerTypeByQueryString(customerTypeValue);
    }
  }

  /**
   * Legge il valore di un filtro dalla query string, cercando tra i campi forniti.
   * Restituisce il primo valore trovato o null.
   */
  private _getFilterValue(...fields: string[]): string | null {
    if (!this.datatableContainerFilterValues) return null;

    for (const field of fields) {
      const match = this.datatableContainerFilterValues.constants
        .find(item => item.Field?.includes(field));
      if (match?.Value?.[0]) return match.Value[0];
    }

    return null;
  }

  /**
   * Pre-seleziona il CustomerType e filtra il customerControl
   * in base al tipo passato dalla query string.
   */
  private _setupCustomerTypeByQueryString(type: string): void {

    // Configura il customerTypeControl per filtrare per il type specifico
    this.customerTypeControl.configureOptions<CtSelectControlOptions>(options => {
      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(false)
        .setLookupFilter(() => {
          const operators = CtModelDatatableOperators.create();
          operators.Pagination?.setSortOrders([CtSortOrderDescriptorParameter.create('Name')]);
          operators.setFilters([
            CTModelDatatableFilter.create().setField('Type').setValue(type).setOperatorType(CtBinaryOperator.Equal)
          ]);
          return operators;
        })
        ?.setLookupResponseMapper((response: CtWebapiGenericResponse<any>) => {
          const result: any[] = response.Result.DataSource ?? [];
          if (!result.length) return [];

          const options = result.map(elm =>
            CtSelectControlValue.create().setLabel(elm.Description).setValue(elm.Code)
          );
          this.customerTypeControl.setValue(options[0].value);
          return options;
        });
    });

    // Configura il customerControl per mostrare solo company del tipo corretto
    this.customerControl.configureOptions<CtSelectControlOptions>(options => {
      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(false)
        .setLookupFilter((value: any) => {
          const operators = CtModelDatatableOperators.create();
          operators.Pagination?.setSortOrders([CtSortOrderDescriptorParameter.create('Name')]);

          const filters: CTModelDatatableFilter[] = [
            CTModelDatatableFilter.create()
              .setField('Type.Type').setValue(type).setOperatorType(CtBinaryOperator.Equal)
          ];

          if (value) {
            const searchableFields = [
              '[FiscalCode]', '[Name]', '[VAT]', '[Surname]', '[ReferenceName]'
            ];
            searchableFields.forEach(field => {
              filters.push(
                CTModelDatatableFilter.create()
                  .setField(`${field} is not null AND Contains(${field}, ?)`)
                  .setValue([value])
                  .setOperatorType(CtBinaryOperator.OrOperator)
              );
            });
          }

          operators.setFilters(filters);
          return operators;
        })
        ?.setLookupResponseMapper((response: CtWebapiGenericResponse<any>) => {
          return (response.Result.DataSource ?? [])
            .map((elm: any) => CtSelectControlValue.create().setLabel(elm.Description).setValue(elm.Code));
        });
    });

    this._applyLeadSourceFilter(+type as CustomerTypeEnum);

    (<CtSelectControlOptions>this.customerTypeControl.options).notifyValuesUpdated();
    (<CtSelectControlOptions>this.customerControl.options).notifyValuesUpdated();
  }

  // ─── Setup da dati esistenti (edit) ──────────────────────────────────────

  /**
   * Popola il form con i dati di un lead esistente ricevuti dal server.
   * Chiamato da `LeadEditComponent` dopo il caricamento via `getInfos`.
   */
  setup<T>(value: T | null): void {
    if (!value) return;

    const form: any = value;

    // Patch dei valori base
    this.configuration.form?.patchValue({
      DocumentDate: form.DocumentDate,
      Email:         form.Email        ?? null,
      LeadInterest:  form.LeadInterest?.Oid,
      LeadSource:    form.LeadSource?.Oid,
      LeadStatus:    form.LeadStatus.Oid,
      Name:          form.Name         ?? null,
      Phone:         form.Phone        ?? null,
      Surname:       form.Surname      ?? null,
      CompanyPlace:  form.CompanyPlace?.Oid,
      Code:          form.Code         ?? null,
      Note:          form.Note         ?? null,
      Prospect:      form.Prospect     ?? null
    });

    // Patch manuale dei controlli non inclusi nel form group di default
    this.surnameControl.setValue(form.Surname ?? null);
    this.sdiControl.setValue(form.SdiCode ?? null);
    this.paymentMethodControl.setValue(form.PaymentMethod ?? null);
    this.billingEmailControl.setValue(form.BillingEmail ?? null);
    this.vatControl.setValue(form.VatNumber ?? null);

    // Precompila il lookup Customer se presente
    if (form.Customer) {
      this.customerControl.setValue(
        CtSelectControlValue
          .create()
          .setLabel(form.Customer.ReferenceName)
          .setValue(form.Customer.Oid)
      );
    }

    // I controlli enumerable vengono settati esplicitamente per garanzia
    this.customerTypeControl.setValue(form?.CustomerType?.Oid ?? null);
    this.leadStatusControl.setValue(form?.LeadStatus?.Oid ?? null);
    this.leadTypeControl.setValue(form.LeadType?.Oid ?? null);

    // Mostra il codice lead nel sottotitolo della pagina se presente
    if (form.Code) {
      this.route.data.subscribe(data => data['extraString'] = `(Codice ${form.Code})`);
    }

    // Controlla se lo stato corrente è "Completato" per abilitare il pulsante work order
    this._checkLeadStatusComplete(form.LeadStatus?.Oid);

    // Carica e pre-compila i campi LeadInformation se configurati e se il lead ha un OID
    if (form.Oid) {
      // Piccolo delay per dare tempo al _initLeadInformationControls (async) di completarsi
      setTimeout(() => this.loadLeadInformationValues(form.Oid), 300);
    }

    // Prepara i tag in anticipo rispetto al customerTypeControl.setValue() che segue.
    // Vengono memorizzati in _pendingTags e applicati da _rebuildForm() dopo il rebuild
    // del FormGroup, garantendo che sopravvivano alla ricreazione del FormControl.
    if (form.Tags?.length) {
      this._pendingTags = form.Tags.map((tag: any) =>
        CtSelectControlValue.create().setLabel(tag.Content).setValue(tag.Oid)
      );
    }
  }

  // ─── Verifica stato lead ──────────────────────────────────────────────────

  /**
   * Verifica se il lead è nello stato "Completato" (Type === 4 / LeadStatusEnum.COMPLETATO).
   * Controlla il flag `leadStatusComplete` che abilita il pulsante "Crea Work Order".
   */
  private _checkLeadStatusComplete(oidStatus: number): void {
    if (!oidStatus) return;

    this.generalService.getList({
      controller: 'LeadStatus'
    }).subscribe((response: CtWebapiGenericResponse<any>) => {

      if (!response.Result.DataSource) return;

      const statusEntry = response.Result.DataSource.find((el: any) => el.Oid === oidStatus);
      this.leadStatusComplete = statusEntry?.Type === 4; // LeadStatusEnum.COMPLETATO

    });
  }

}
